import React, {useEffect, useState} from 'react';
import Layout from '../components/Layout';
import {api} from '../../api';

export default function Settings() {
  const [ipRestrictionEnabled, setIpRestrictionEnabled] = useState(false);
  const [allowedIps, setAllowedIps] = useState('');
  const [restrictedEmployeeIds, setRestrictedEmployeeIds] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    api('/timesheets/settings').then(r => r.ok ? r.json() : null).then(data => {
      if (data) {
        setIpRestrictionEnabled(data.ipRestrictionEnabled || false);
        setAllowedIps((data.allowedIps || []).join('\n'));
        setRestrictedEmployeeIds(data.restrictedEmployeeIds || []);
      }
    });
    api('/hcm/employees').then(r => r.ok ? r.json() : []).then(setEmployees);
  }, []);

  const handleSave = () => {
    setSaving(true);
    setMessage(null);
    const ips = allowedIps.split('\n').map(s => s.trim()).filter(Boolean);
    api('/timesheets/settings', {
      method: 'PUT',
      body: JSON.stringify({ ipRestrictionEnabled, allowedIps: ips, restrictedEmployeeIds })
    }).then(r => {
      if (r.ok) setMessage('Settings saved.');
      else setMessage('Failed to save settings.');
      setSaving(false);
    });
  };

  const toggleEmployee = (id) => {
    setRestrictedEmployeeIds(prev =>
      prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id]
    );
  };

  return (
    <Layout>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Timesheet Settings</h2>
      <div className="bg-white rounded-lg shadow p-6 max-w-2xl">
        <div className="mb-6">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={ipRestrictionEnabled}
              onChange={e => setIpRestrictionEnabled(e.target.checked)}
              className="w-5 h-5 text-teal-600 rounded"
            />
            <span className="text-gray-900 font-medium">Restrict clock-in to specific IP addresses</span>
          </label>
          <p className="text-sm text-gray-500 mt-1 ml-8">
            When enabled, selected employees can only clock in from the IP addresses listed below.
          </p>
        </div>

        {ipRestrictionEnabled && (
          <>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Allowed IP Addresses
              </label>
              <textarea
                value={allowedIps}
                onChange={e => setAllowedIps(e.target.value)}
                rows={5}
                placeholder={"192.168.1.100\n10.0.0.0/24\n203.0.113.50"}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-teal-500 focus:border-teal-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter one IP address or CIDR range per line.
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Apply to Employees
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Select specific employees. If none are selected, the restriction applies to all employees.
              </p>
              <div className="border border-gray-300 rounded-md max-h-48 overflow-y-auto">
                {employees.map(emp => (
                  <label key={emp.id} className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={restrictedEmployeeIds.includes(emp.id)}
                      onChange={() => toggleEmployee(emp.id)}
                      className="w-4 h-4 text-teal-600 rounded mr-3"
                    />
                    <span className="text-sm text-gray-800">{emp.firstName} {emp.lastName}</span>
                  </label>
                ))}
                {employees.length === 0 && (
                  <div className="px-3 py-4 text-sm text-gray-500 text-center">No employees found.</div>
                )}
              </div>
            </div>
          </>
        )}

        {message && (
          <div className={`mb-4 text-sm ${message.includes('Failed') ? 'text-red-600' : 'text-green-600'}`}>
            {message}
          </div>
        )}

        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-teal-600 text-white px-4 py-2 rounded-md hover:bg-teal-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </Layout>
  );
}
